import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Observable, ReplaySubject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {UserPermissions} from '../../../shared/interface/user-permissions';
import {AuthService} from '../../../shared/services/auth.service';
import {Router} from '@angular/router';
import {PersonalDataChange} from "../../../shared/interface/personalDataChange";
import {Profile} from "../../../shared/interface/profile";
import {PersonalDataChangeService} from "../../../shared/services/personal-data-change.service";
import {
  DICT_TYPE_TEMPLATE_TYPE,
  DictService,
  PERSONAL_DATA_CHANGE_STATUS,
  PERSONAL_DATA_CHANGE_STATUS_COMPLETED,
  PERSONAL_DATA_CHANGE_STATUS_SENT_TO_IC,
  TEMPLATE_TYPE_PERSONAL_DATA_CHANGE,
} from "../../../shared/services/dict.service";
import {Uuid} from "../../../shared/interface/response/uuid";
import {JobResultDialogComponent} from "../../../shared/components/job-result-dialog/job-result-dialog.component";
import {HttpErrorResponse} from "@angular/common/http";
import {TemplateService} from "../../../shared/services/template.service";
import {ConfirmDialogComponent} from "../../../shared/components/confirm-dialog/confirm-dialog.component";
import {Dictionary} from "../../../shared/interface/dictionary";

@Component({
  selector: 'app-personal-data-change-details-view',
  templateUrl: './personal-data-change-details-view.component.html',
  styleUrls: ['./personal-data-change-details-view.component.scss']
})
export class PersonalDataChangeDetailsViewComponent implements OnInit, OnDestroy {

  @Input() personalDataChange: Observable<PersonalDataChange>;
  @Output() modifyClicked = new EventEmitter<void>();

  readonly PERSONAL_DATA_CHANGE_STATUS_SENT_TO_IC = PERSONAL_DATA_CHANGE_STATUS_SENT_TO_IC;
  personalDataChangeObject?: PersonalDataChange;
  userPermissions: ReplaySubject<UserPermissions>;
  profile: Profile;
  generatingDocument = false;
  formErrors = {};
  processingRequest = false;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private auth: AuthService,
    private dialog: MatDialog,
    private dictService: DictService,
    private templateService: TemplateService,
    private personalDataChangeService: PersonalDataChangeService,
    private snack: MatSnackBar,
    private router: Router,
  ) {
    this.userPermissions = auth.getUserPermissions();
  }

  ngOnInit(): void {
    this.auth.getProfile()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((profile: Profile) => {
        this.profile = profile;
      });
    this.personalDataChange
      .pipe(takeUntil(this.destroyed$))
      .subscribe((personalDataChange: PersonalDataChange) => {
        this.personalDataChangeObject = personalDataChange;
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  modify(): void {
    this.modifyClicked.emit();
  }

  generate(): void {
    this.generatingDocument = true;
    this.dictService.getId(TEMPLATE_TYPE_PERSONAL_DATA_CHANGE, DICT_TYPE_TEMPLATE_TYPE)
      .subscribe((dictId: number) => {
        this.templateService.generate({
          type: dictId,
          personalDataChange: this.personalDataChangeObject.uuid,
        }).subscribe((response: Uuid) => {
          const dialogRef = this.dialog.open(JobResultDialogComponent, {
            disableClose: true,
            width: '500px',
            data: {jobId: response.uuid, showProgress: false},
          });

          dialogRef.afterClosed().subscribe(result => {
            this.generatingDocument = false;
          });
        }, (error: HttpErrorResponse) => {
          this.generatingDocument = false;
          if (error.status === 404) {
            this.snack.open('Brak szablonów dla tego typu dokumentu', '', {duration: 2000});
            return;
          }

          this.snack.open('Przy generowaniu dokumentów wystąpił błąd', error.status.toString(), {duration: 2000});
        });
      });
  }

  delete(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć wniosek?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.personalDataChangeService.delete(this.personalDataChangeObject.uuid)
          .subscribe(() => {
            this.snack.open('Pomyślnie usunięto wniosek', '', {duration: 2000});
            this.router.navigate(['/zmiana-danych-osobowych/lista']);
          });
      }
    });
  }

  changeStatusToCompleted(): void {
    this.processingRequest = true;
    this.dictService.get(PERSONAL_DATA_CHANGE_STATUS_COMPLETED, PERSONAL_DATA_CHANGE_STATUS)
      .subscribe((dict: Dictionary) => {

        this.personalDataChangeService.updateStatus(dict, this.personalDataChangeObject.uuid)
          .subscribe(() => {
            this.snack.open('Pomyślnie zmieniono status na Zrealizowany', '', {
              duration: 2000,
            });
            this.processingRequest = false;

            this.personalDataChangeObject.status = dict;
          }, (error: HttpErrorResponse) => {
            this.processingRequest = false;
            this.snack.open('Podczas zmiany statusu wystąpił błąd', error.status.toString(), {
              duration: 2000,
            });
          });
      });
  }
}
